import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  InputGroup,
  ListboxGroup,
  useLatinCharacterValidation
} from '@/common'
import { goodsFamilies } from '../../api/InstallationModel'
import { GoodsFamily } from '@/common'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import ReactCountryFlag from 'react-country-flag'
import { usePermissions } from '@/shared/hooks/usePermissions'

export default function GeneralInfoCard({
  id,
  state,
  onChange,
  organizationPlaceOfRegistration
}: {
  id: string
  onChange: (payload: {
    name: string
    localName: string
    goodsFamily: string | GoodsFamily
  }) => void
  state: {
    name: string
    localName: string
    goodsFamily: string | GoodsFamily
  }
  organizationPlaceOfRegistration?: string | null
}) {
  const { t } = useTranslation()
  const doesOrganizationRequiresLocalAddress = useDoesOrganizationRequiresLocalAddress(
    organizationPlaceOfRegistration
  )
  const { name, goodsFamily, localName } = state

  const { checkPermission } = usePermissions()
  const hasInternalPermission = checkPermission('internal', 'create')
  // Validate English name field for Latin characters
  const { error: nameLatinError } = useLatinCharacterValidation(
    name,
    t('common:errors.latinCharacters')
  )

  const goodsFamilyOptions: {
    value: GoodsFamily
    label: string
    disabled?: boolean
    tooltip?: string
  }[] = useMemo(() => {
    return goodsFamilies.map(goodsFamily => ({
      value: goodsFamily.value,
      label: t(`installations:fields.goodsFamily.options.${goodsFamily.value}`),
      disabled: !goodsFamily.isReportingEnabled && !hasInternalPermission,
      tooltip:
        !goodsFamily.isReportingEnabled && !hasInternalPermission
          ? t('common:outsidePlanTooltip')
          : undefined
    }))
  }, [t, hasInternalPermission])

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      if (evt.target.id === 'name' && !doesOrganizationRequiresLocalAddress) {
        onChange({ ...state, localName: evt.target.value, name: evt.target.value })
      } else {
        onChange({ ...state, [evt.target.id]: evt.target.value })
      }
    },
    [state, onChange, doesOrganizationRequiresLocalAddress]
  )

  const onGoodsFamilyChange = useCallback(
    (value: string) => {
      onChange({ ...state, goodsFamily: value as GoodsFamily })
    },
    [state, onChange]
  )

  return (
    <div id={id} className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle title={t('installations:create.sections.generalInfo.title')}></CardTitle>
        </CardHeader>
        <CardBody className="space-y-4 w-3/5">
          {!doesOrganizationRequiresLocalAddress ? (
            <InputGroup
              id="name"
              label={t('installations:fields.name.label')}
              value={name}
              tooltip={t('installations:fields.name.tooltip')}
              error={nameLatinError || undefined}
              onChange={onInputChange}
            />
          ) : (
            <div className="flex gap-4">
              <div className="flex-1">
                <InputGroup
                  id="localName"
                  label={
                    <>
                      {t('installations:fields.localName.label')}
                      <ReactCountryFlag countryCode={organizationPlaceOfRegistration || ''} svg />
                    </>
                  }
                  value={localName}
                  tooltip={t('installations:fields.localName.tooltip')}
                  onChange={onInputChange}
                />
              </div>
              <div className="flex-1">
                <InputGroup
                  id="name"
                  label={
                    <>
                      {t('installations:fields.name.label')}
                      <ReactCountryFlag countryCode={'gb'} svg />
                    </>
                  }
                  value={name}
                  tooltip={t('installations:fields.name.tooltip')}
                  error={nameLatinError || undefined}
                  onChange={onInputChange}
                />
              </div>
            </div>
          )}

          <ListboxGroup
            label={t('installations:fields.goodsFamily.label')}
            tooltip={t('installations:fields.goodsFamily.tooltip')}
            options={goodsFamilyOptions}
            value={goodsFamily}
            onChange={onGoodsFamilyChange}
          />
        </CardBody>
      </Card>
    </div>
  )
}
